import React from 'react';
import {
  AppsListDetail24Regular,
  Beach24Regular,
  Briefcase24Regular,
  Building24Regular,
  Calendar24Regular,
  CalendarWeekNumbers24Regular,
  ChartMultiple24Regular,
  ClipboardTaskListLtr24Regular,
  Delete24Regular,
  PersonClock24Regular,
  Gauge24Regular,
  Key24Regular,
  People24Regular,
  PersonFeedback24Regular,
  PersonShield24Regular,
  SignOut24Regular,
  WeatherMoon24Regular,
  WeatherSunny24Regular,
} from '@fluentui/react-icons';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TimeSelect from './TimeSelect';

interface NavigationProps {
  isMobileOpen: boolean;
  onToggle: () => void;
  onNavigate: () => void;
  isDarkMode: boolean;
  onToggleTheme: () => void;
}

type NavItem = {
  path: string;
  label: string;
  icon: React.ReactNode;
  adminOnly?: boolean;
  reportOnly?: boolean;
  projectPlanningOnly?: boolean;
};

const navItems: NavItem[] = [
  { path: '/', label: 'Dashboard', icon: <ChartMultiple24Regular /> },
  { path: '/admin-dashboard', label: 'Admin dashboard', icon: <Gauge24Regular />, adminOnly: true },
  { path: '/entries', label: 'Entries', icon: <ClipboardTaskListLtr24Regular /> },
  { path: '/api-keys', label: 'API keys', icon: <Key24Regular /> },
  { path: '/calendar', label: 'Calendar', icon: <Calendar24Regular /> },
  { path: '/weekly', label: 'Weekly', icon: <CalendarWeekNumbers24Regular /> },
  { path: '/reports', label: 'Reports', icon: <AppsListDetail24Regular />, reportOnly: true },
  { path: '/leads', label: 'Leads', icon: <Briefcase24Regular />, reportOnly: true },
  { path: '/customers', label: 'Customers', icon: <Building24Regular />, reportOnly: true },
  { path: '/dayoffs', label: 'Dayoffs', icon: <Beach24Regular />, adminOnly: true },
  { path: '/member-time-summary', label: 'Member time', icon: <PersonClock24Regular />, adminOnly: true },
  { path: '/deleted-entries', label: 'Deleted entries', icon: <Delete24Regular />, adminOnly: true },
  { path: '/project-tasks', label: 'Project tasks', icon: <PersonFeedback24Regular />, adminOnly: true },
  { path: '/resource-planning', label: 'Resource planning', icon: <People24Regular />, adminOnly: true },
  { path: '/project-planning', label: 'Project planning', icon: <AppsListDetail24Regular />, projectPlanningOnly: true },
  { path: '/admin-api-keys', label: 'All API keys', icon: <PersonShield24Regular />, adminOnly: true },
  { path: '/employees', label: 'Members', icon: <People24Regular />, adminOnly: true },
];

const Navigation: React.FC<NavigationProps> = ({
  isMobileOpen,
  onToggle,
  onNavigate,
  isDarkMode,
  onToggleTheme,
}) => {
  const { user, logout, isAdmin, actualIsAdmin, canViewAllReports, canManageProjectPlanning, roleViewMode, setRoleViewMode } = useAuth();
  const location = useLocation();
  const displayName = user?.name?.trim() || user?.email || 'User';
  const displayEmail = user?.email?.trim() || 'No email available';
  const roleLabel = roleViewMode === 'ReportViewer' ? 'Report Viewer mode' : (isAdmin ? 'Admin mode' : (canManageProjectPlanning ? 'Project Manager' : 'User mode'));

  const isActive = (path: string) => location.pathname === path || (path === '/' && location.pathname === '/dashboard') || (path !== '/' && location.pathname.startsWith(`${path}/`));

  return (
    <div className={`sidebar ${isMobileOpen ? 'sidebar-open' : ''}`}>
      <div className="sidebar-mobile-header">
        <button type="button" className="mobile-close-button" onClick={onToggle} aria-label="Close navigation">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>

      <div className="time-sidebar-brand">
        <Link to="/" className="time-brand-link" onClick={onNavigate}>
          <span className="devrain-mark" aria-hidden="true"><span /><span /></span>
          <span>DevRain Time</span>
        </Link>
        <button
          type="button"
          className="icon-button fluent-icon-button"
          onClick={onToggleTheme}
          aria-label={isDarkMode ? 'Switch to light theme' : 'Switch to dark theme'}
          title={isDarkMode ? 'Light mode' : 'Dark mode'}
        >
          {isDarkMode ? <WeatherSunny24Regular aria-hidden="true" /> : <WeatherMoon24Regular aria-hidden="true" />}
        </button>
      </div>

      <nav className="time-nav" aria-label="Time tracking navigation">
        <ul>
          {navItems.filter(item => (!item.adminOnly || isAdmin) && (!item.reportOnly || canViewAllReports) && (!item.projectPlanningOnly || canManageProjectPlanning)).map((item) => (
            <li key={item.path}>
              <Link
                to={item.path}
                className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                onClick={onNavigate}
                aria-current={isActive(item.path) ? 'page' : undefined}
              >
                <span className="nav-icon" aria-hidden="true">{item.icon}</span>
                <span>{item.label}</span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <div className="time-user-panel">
        <div className="time-user" aria-label="Signed in user">
          <div className="time-user-avatar">{displayName.charAt(0).toUpperCase()}</div>
          <div className="time-user-text">
            <div className="time-user-name">{displayName}</div>
            <div className="time-user-email">{displayEmail}</div>
            <div className="time-user-role">{roleLabel}</div>
          </div>
        </div>
        {actualIsAdmin && (
          <label className="role-view-switch" htmlFor="role-view-mode">
            <span>View as</span>
            <TimeSelect
              inputId="role-view-mode"
              ariaLabel="View as"
              options={[
                { value: 'Admin', label: 'Admin' },
                { value: 'User', label: 'User' },
                { value: 'ReportViewer', label: 'Report Viewer' },
              ]}
              value={roleViewMode}
              onChange={(value) => {
                setRoleViewMode(value === 'User' || value === 'ReportViewer' ? value : 'Admin');
                onNavigate();
              }}
              placeholder="View as"
            />
          </label>
        )}
        <button
          className="btn btn-outline-danger logout-button"
          type="button"
          onClick={() => {
            logout();
            onNavigate();
          }}
        >
          <SignOut24Regular aria-hidden="true" />
          Logout
        </button>
      </div>
    </div>
  );
};

export default Navigation;
